import {
  Divider,
  IconButton,
  makeStyles,
  MenuItem,
  Tooltip,
} from "@material-ui/core";
import { FilterList, FormatListBulleted } from "@material-ui/icons";
import React from "react";
import { NotificationsDataState } from "../redux/state";
import { notificationTypesService } from "../services";
import { QueryParams } from "@ofqual/portal-core/core/types";
import { useLookup, useSearch } from "@ofqual/portal-core/core/hooks";
import { Pager } from "@ofqual/portal-components/components/Pager";
import {
  FilterMenu,
  FilterCheckbox,
} from "@ofqual/portal-components/components/ResponseFilter";
import { Select } from "@ofqual/portal-components/components/Select";
import { DatePicker } from "@ofqual/portal-components/components/DatePicker";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { LcmFilter } from "./filterSections/LcmFilter";
import { useSelector } from "react-redux";
import { authSelector } from "../redux/selectors";
import { UserRole } from "../services/domain/userRole";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    alignItems: "center",
    position: "fixed",
    width: "650px",
    backgroundColor: "#fff",
    borderBottom: "1px solid #d5d5d5",
    zIndex: 1001,
  },
  pager: {
    marginLeft: "auto",
  },
  button: {
    color: "inherit",

    "&:disabled": {
      color: "#999",
    },
  },
  menuItem: {
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
}));

type NotificationListHeaderProps = {
  isBulkMode: boolean;
  toggleBulkMode: (isBulkMode: boolean) => void;
  dataState: NotificationsDataState;
  haveItems: boolean;
  navigate: (nextParams: QueryParams) => void;
};

export const NotificationListHeader: React.FC<NotificationListHeaderProps> = ({
  isBulkMode,
  toggleBulkMode,
  dataState,
  haveItems,
  navigate,
}) => {
  const { params: searchParams } = dataState;
  const classes = useStyles();
  const { roles } = useSelector(authSelector);

  const fullRoles = new Set(roles);
  const isLcmUser: boolean = fullRoles.has(UserRole.ITAdmin) || fullRoles.has(UserRole.LegalCaseManager) || fullRoles.has(UserRole.LegalLpm) ||
    fullRoles.has(UserRole.LegalDirector) || fullRoles.has(UserRole.LegalGc);

  const toggleButtonColour = haveItems
    ? isBulkMode
      ? "primary"
      : "secondary"
    : "inherit";

  const [notificationTypes] = useLookup(notificationTypesService);

  const search = useSearch({
    navigate,
    searchParams,
  });

  return (
    <div className={classes.container}>
      <Tooltip title="Bulk Select" aria-label="Bulk Select">
        <IconButton
          onClick={() => toggleBulkMode(!isBulkMode)}
          disabled={!haveItems}
          className={classes.button}
          data-test="bulk-mode"
        >
          <FormatListBulleted color={toggleButtonColour}></FormatListBulleted>
        </IconButton>
      </Tooltip>

      <FilterMenu
        Icon={FilterList}
        dataTest="notifications-filter"
        search={search}
        classNameButton={classes.button}
      >
        {(workingParams, dirtyParams, addParam) => (
          <form>
            <MenuItem className={classes.menuItem}>
              <DatePicker
                label="From:"
                value={workingParams.fromDate || null}
                onChange={(val) => addParam("fromDate", val)}
                onClear={() => addParam("fromDate", undefined)}
                returnDayBoundary="startOfDay"
              />
            </MenuItem>
            <MenuItem className={classes.menuItem}>
              <DatePicker
                label="To:"
                value={workingParams.toDate || null}
                onChange={(val) => addParam("toDate", val)}
                onClear={() => addParam("toDate", undefined)}
                returnDayBoundary="endOfDay"
              />
            </MenuItem>
            <Divider />
            <FilterCheckbox
              isChecked={workingParams.includeRead === "true"}
              name="IncludeRead"
              text="Include read notifications"
              onChange={() =>
                addParam(
                  "includeRead",
                  workingParams.includeRead ? undefined : "true"
                )
              }
            />
            <Divider />
            <MenuItem className={classes.menuItem}>
              <Select
                label="Notification types"
                data-test="notification-types-filter"
                value={workingParams.type}
                onChange={(e) => addParam("type", e.target.value)}
              >
                {notificationTypes}
              </Select>
            </MenuItem>
            <Divider />
            <MenuItem className={classes.menuItem}>
              <TextField
                label="Search"
                variant="outlined"
                value={workingParams.searchText}
                onChange={(e) => addParam("searchText", e.target.value)}
              />
            </MenuItem>
            {isLcmUser && <LcmFilter workingParams={workingParams} addParam={addParam} />}
          </form>
        )}
      </FilterMenu>

      <span className={classes.pager}>
        <Pager
          state={dataState}
          navigateToNextPage={navigate}
          showRowsPerPage={false}
          showRecordCount={false}
          pageLabel="Notifications Page"
        />
      </span>
    </div>
  );
};
